// src/lib/location.js
// Phase 37 — extracted from src/App.jsx (lines 653–730 originally; current ~587–657).
//
// Scope: UK postcode validation/normalisation + browser geolocation +
//        reverse geocoding via postcodes.io.
// Public exports: UK_POSTCODE_RE, UK_POSTCODE_OUTWARD_RE,
//                 isValidUkPostcode, normaliseUkPostcode, outwardCode,
//                 requestGeolocation, reverseGeocode.
// Module state: none.

// UK postcode shape regex (Royal Mail canonical; case/space-insensitive).
// Full postcode: letters + digit + optional letter/digit + optional space + digit + 2 letters.
export const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;
// Outward-code-only (prefix) accepted as a valid entry.
export const UK_POSTCODE_OUTWARD_RE = /^[A-Z]{1,2}\d[A-Z\d]?$/i;

export function isValidUkPostcode(value) {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (!v) return false;
  return UK_POSTCODE_RE.test(v) || UK_POSTCODE_OUTWARD_RE.test(v);
}

export function normaliseUkPostcode(value) {
  if (typeof value !== "string") return "";
  const v = value.trim().toUpperCase().replace(/\s+/g, "");
  if (UK_POSTCODE_OUTWARD_RE.test(v)) return v;
  if (UK_POSTCODE_RE.test(v)) return v.slice(0, -3) + " " + v.slice(-3);
  return value.trim().toUpperCase();
}

export function outwardCode(value) {
  if (typeof value !== "string") return "";
  const v = normaliseUkPostcode(value);
  if (UK_POSTCODE_OUTWARD_RE.test(v)) return v;
  const idx = v.lastIndexOf(" ");
  return idx > 0 ? v.slice(0, idx) : v;
}

// Returns Promise<{lat:number, lon:number}>. Rejects with { code, message } on any failure.
export function requestGeolocation(timeoutMs = 10000) {
  return new Promise((resolve, reject) => {
    if (!navigator || !navigator.geolocation) {
      reject({ code: "UNAVAILABLE", message: "Geolocation is not available on this device." });
      return;
    }
    // iOS Safari (and most modern browsers) silently block geolocation on insecure origins.
    // Detect up front so we show a clear message instead of a misleading "permission denied".
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      reject({ code: "INSECURE_CONTEXT", message: "Location needs a secure (https) connection. Enter a postcode manually." });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      (err) => {
        const codeMap = { 1: "PERMISSION_DENIED", 2: "POSITION_UNAVAILABLE", 3: "TIMEOUT" };
        reject({ code: codeMap[err.code] || "UNKNOWN", message: err.message || "Could not detect location." });
      },
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60000 }
    );
  });
}

// Reverse-geocode via postcodes.io nearest endpoint. No API key. Rejects on non-UK / empty / network error.
export async function reverseGeocode(lat, lon) {
  const url = `https://api.postcodes.io/postcodes?lon=${encodeURIComponent(lon)}&lat=${encodeURIComponent(lat)}`;
  let res;
  try {
    res = await fetch(url, { method: "GET" });
  } catch {
    throw { code: "NETWORK", message: "Could not reach location service." };
  }
  if (!res.ok) throw { code: "NETWORK", message: `Location service returned ${res.status}.` };
  let body;
  try { body = await res.json(); } catch { throw { code: "NETWORK", message: "Bad response from location service." }; }
  const first = body && Array.isArray(body.result) ? body.result[0] : null;
  if (!first || !first.outcode) throw { code: "NO_MATCH", message: "We couldn't find a UK postcode near you." };
  return { outcode: String(first.outcode).toUpperCase() };
}
